import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Chip, IconButton } from "@mui/material";
import ActionButton from "../../components/common/buttons/ActionButton";
import Grid from '@mui/material/Grid';
import { Popconfirm, message } from "antd";
import moment from "moment";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axios-instance";
import Card from "../../components/common/Card";
import Fillter from "../../components/common/Fillter";
import MuiTable from "../../components/common/MuiTable";
import FilterNavbar from "../../components/filter-navbar/FilterNavbar";
import { useCountries } from "../../hooks/useCountries";
import { useMe } from "../../auth/MeContext";
import { PERM } from "../../auth/permissions";
import { Column, ROLE, SearchForm, User } from "../../utils/constants";
import { STATUS_PILL_CLASSES } from "../../theme/statusColors";
import { KeyValueObject } from "../../utils/models";
import { pathAPI } from "../../utils/pathApi";
import AddAdmin from "./add-admin/AddAdmin";
import defaultAvatar from "../../assets/default/default-user-avatar.svg";

const UserAdmin = () => {
  const { t } = useTranslation();
  const [{ data: countries }] = useCountries();
  var currentLanguage: any = localStorage.getItem("language");

  const currentUserId = localStorage.getItem("current_user_id") || "en";
  const { can } = useMe();
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState([]);
  const filterConsts: any = [];
  const [openFilter, setOpenFilter] = useState(false);
  const [adminDetailId, setAdminInfo] = useState(null);
  const [isLoadingTable, setLoadingTable] = useState(true);
  const [isEditAdmin, setIsEditAdmin] = useState(false);
  const countryData = countries?.map((item: any) => {
    return {
      key: item.id,
      value: item.name || item.i18n_names?.[currentLanguage] || item.code,
    };
  });

  const header: Column[] = [
    {
      id: "name",
      label: `${t("common.table.name")}`,
      minWidth: 170,
      align: "center",
    },
    {
      id: "avatar",
      label: `${t("common.table.avatar")}`,
      minWidth: 150,
      align: "center",
      format: (avatarUrl: string) => (
        <img
          src={avatarUrl || defaultAvatar}
          alt="avatar"
          className="w-14 h-14 rounded-lg object-cover"
        />
      ),
    },
    {
      id: "email",
      label: `${t("common.table.email")}`,
      minWidth: 170,
      align: "center",
    },
    {
      id: "role",
      label: `${t("common.table.roles")}`,
      minWidth: 5,
      align: "center",
      format: (role: { code: string; name: string }) => {
        const value = role?.code;
        switch (value) {
          case ROLE.SUPER_ADMIN:
            return (
              <div className="text-violet-500 font-bold">
                Super admin
              </div>
            );
          case ROLE.ADMIN:
            return (
              <div className="text-pink-500 font-bold">
                Admin
              </div>
            );
          case ROLE.LEADER:
            return (
              <div className="text-green-500 font-bold">
                Leader
              </div>
            );
          default:
            // Custom roles created by the super admin
            return (
              <div className="p-1 text-sky-600 font-bold">
                {role?.name || "Unknown Role"}
              </div>
            );
        }
      },
    },
    {
      id: "locations",
      label: `${t("common.table.locations")}`,
      minWidth: 20,
      align: "center",
      format: (value: any) => {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5 }}>
            {value.map((item: string) => (
              <Chip key={item} label={item} size="small" color="info" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      id: "can_edit",
      label: `${t("common.table.can_edit")}`,
      minWidth: 5,
      align: "center",
      format: (value: boolean) => {
        return (
          <div
            className={`${value ? STATUS_PILL_CLASSES.SUCCESS : STATUS_PILL_CLASSES.DRAFT} font-bold rounded-lg`}
          >
            {value.toString()}
          </div>
        );
      },
    },
    {
      id: "created_date",
      label: `${t("common.table.created_date")}`,
      minWidth: 170,
      align: "center",
      format: (value) => {
        return <div>{moment(value).format("YYYY/MM/DD HH:mm:ss")}</div>;
      },
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 100,
      align: "center",
      format: (id: number) => {
        return (
          <Box display={"flex"} alignItems={"center"} gap={2}>
            <Box
              size={"small"}
              component={IconButton}
              disabled={loadingDelete || !can(PERM.ADMIN_USER_UPDATE)}
              onClick={() => handleClickAdminDetail(id)}
            >
              <EditIcon color="success" />
            </Box>
            <Popconfirm
              title={t("help_support.delete_text")}
              okText={t("btn.confirm")}
              cancelText={t("btn.cancel")}
              onConfirm={() => handleDelete(id)}
              okButtonProps={{
                style: { background: "#a11f4c", color: "#fff", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box
                size={"small"}
                component={IconButton}
                disabled={loadingDelete || Number(currentUserId) === id || !can(PERM.ADMIN_USER_DELETE)}
              >
                <DeleteIcon
                  color={
                    loadingDelete || Number(currentUserId) === id || !can(PERM.ADMIN_USER_DELETE)
                      ? "disabled"
                      : "error"
                  }
                />
              </Box>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  const handleClickAdminDetail = async (id: any) => {
    setAdminInfo(id);
    setOpenModal(true);
    setIsEditAdmin(true);
  };
  const [request, setRequest] = useState<SearchForm>({
    page: 0,
    size: 20,
    search: "",
    filter: "",
    countryId: "",
  });

  const openModalAddNew = () => {
    setOpenModal(true);
    setIsEditAdmin(false);
  };

  const closeModal = () => {
    setOpenModal(false);
    setAdminInfo(null);
  };

  const handleDelete = async (id: any) => {
    setLoadingDelete(true);
    await axiosInstance.delete(`${pathAPI.adminUsers}/${id}`);
    setLoadingDelete(false);
    message.success(`${t("help_support_detail.mess_success")}`);
    reloadDataTable();
  };

  const handleOnChangeCountry = (e: KeyValueObject | null) => {
    const countryData = e ? e.key : "";
    setRequest((previousRequest) => ({
      ...previousRequest,
      countryId: countryData,
    }));
  };

  const handleChangeSearch = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRequest((previousRequest) => ({
        ...previousRequest,
        search: e.target.value,
      }));
  };

  const reloadDataTable = async () => {
    setLoadingTable(true);
    await axiosInstance
      .get(pathAPI.adminUsers, { params: request })
      .then((res: any) => {
        setLoadingTable(false);
        if (res.data.data) {
          const customData = res.data.data.map((item: User) => {
            return {
              name: item.full_name,
              email: item.email,
              role: {
                code: item.role_code || item.role.name,
                name: item.role_name || item.role.name,
              },
              locations: item.locations.map((lo) => lo.name),
              created_date: item.created_date,
              avatar: item.avatar,
              action: item.id,
              can_edit: item.can_edit,
            };
          });
          setData(customData);
        }
      })
      .catch((err: any) => {
        console.log("====== reload data table error: ", err);
        setData([]);
      })
      .finally(() => {
        setLoadingTable(false);
      });
  };

  useEffect(() => {
    reloadDataTable();
  }, []);

  // Check if the current user can add admins/leaders
  const canAddUser = can(PERM.ADMIN_USER_CREATE);

  return (
    <Card className="user">
      <Grid container spacing={2} className="mb-2 m-auto">
        <Grid size={{ xs: 12, md: canAddUser ? 10 : 12, lg: canAddUser ? 10 : 12 }}>
          <Fillter
            inputPlaceholder="Search by name or email"
            handleOnChangeDropDown={(value) => handleOnChangeCountry(value)}
            countryData={countryData}
            showCountryFilter={true}
            showSearchFilter={true}
            onChangeSearch={handleChangeSearch}
            showBtnSearch
            handleClickBtn={() => reloadDataTable()}
          />
        </Grid>
        {canAddUser && (
          <Grid
            size={{ xs: 12, md: 2, lg: 2 }}
            sx={{ display: "flex", justifyContent: "flex-end", height: 40, margin: 'auto 0' }}
          >
            <ActionButton appSize="large" startIcon={<AddIcon />} onClick={openModalAddNew}>
              {t("btn.add")}
            </ActionButton>
          </Grid>
        )}
      </Grid>
      <MuiTable
        header={header}
        data={data}
        isLoading={isLoadingTable}
      ></MuiTable>
      <FilterNavbar
        openFilter={openFilter}
        options={filterConsts}
        onCloseFilter={() => setOpenFilter(false)}
        displayStatus={false}
        displayLocation={true}
      />
      <AddAdmin
        controlModal={openModal}
        closeModal={closeModal}
        reLoadDataTable={reloadDataTable}
        adminId={adminDetailId}
        isEditAdmin={isEditAdmin}
      ></AddAdmin>
    </Card>
  );
};
export default UserAdmin;
